import { backofficeAuthGetMe, backofficeAuthGetSession } from '@repo/sdk/gen'
import { type QueryClient, queryOptions } from '@tanstack/react-query'
import { makeQueryKeys } from '@/lib/query-keys'

/**
 * Session freshness pact: the session query has staleTime Infinity, so route
 * guards read it cache-first and never await the network mid-session (the
 * router shows the pending page at 0ms). Every point where the auth state has
 * just changed server-side — the post-sign-in flows — revalidates through
 * this: fetchQuery + staleTime 0 refetches and awaits, while ensureQueryData
 * would return the stale cached value and loop the login. Logout and the 401
 * recovery revalidate by clearing the cache instead.
 */
export function fetchFreshSession(queryClient: QueryClient) {
	return queryClient.fetchQuery({
		...adminSessionQueryOptions(),
		staleTime: 0
	})
}

export function meQueryOptions() {
	return queryOptions({
		queryKey: makeQueryKeys.admins.me(),
		queryFn: async () => {
			const response = await backofficeAuthGetMe({ body: {} })
			return response.data
		}
	})
}

export function adminSessionQueryOptions() {
	return queryOptions({
		/** Keep the CSRF token available while the HttpOnly session cookie is valid. */
		gcTime: Number.POSITIVE_INFINITY,
		/** Refetched only via auth invalidation (login/logout) or the 401 cache clear, never per navigation. */
		staleTime: Number.POSITIVE_INFINITY,
		queryKey: makeQueryKeys.auth.session(),
		queryFn: async () => {
			const data = await backofficeAuthGetSession()

			if (data.sessionType === 'mfa_pending') {
				return { sessionType: 'mfa_pending' as const, mfa: data.mfa }
			}

			if (data.sessionType !== 'authenticated') {
				return { sessionType: 'unauthenticated' as const }
			}

			if (!(data.session && data.admin && data.csrfToken)) {
				throw new Error('Authenticated session payload is incomplete')
			}

			return {
				...data,
				sessionType: 'authenticated' as const,
				csrfToken: data.csrfToken,
				session: data.session,
				admin: data.admin
			}
		}
	})
}
